import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const ValeurCards = ({ data, icon, color = "purple", isDark = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);

  const getColorClasses = (baseColor) => {
    const colorMap = {
      blue: {
        bgFrom: 'from-blue-400', bgTo: 'to-indigo-600',
        text: 'text-blue-600', darkText: 'text-blue-400',
        glow: 'shadow-blue-500/30', border: 'border-blue-500/20'
      },
      purple: {
        bgFrom: 'from-purple-400', bgTo: 'to-fuchsia-600',
        text: 'text-purple-600', darkText: 'text-purple-400',
        glow: 'shadow-purple-500/30', border: 'border-purple-500/20'
      },
      pink: {
        bgFrom: 'from-pink-400', bgTo: 'to-rose-600',
        text: 'text-pink-600', darkText: 'text-pink-400',
        glow: 'shadow-pink-500/30', border: 'border-pink-500/20'
      },
      green: {
        bgFrom: 'from-emerald-400', bgTo: 'to-teal-600',
        text: 'text-emerald-600', darkText: 'text-emerald-400',
        glow: 'shadow-emerald-500/30', border: 'border-emerald-500/20'
      }
    };
    return colorMap[baseColor] || colorMap.purple;
  };

  const colors = getColorClasses(color);

  const handleScroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8;
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  // Card theme applied based on layout
  const cardBg = isDark 
    ? 'bg-slate-800/80 backdrop-blur-xl border border-slate-700/50' 
    : 'bg-white/90 backdrop-blur-xl border border-gray-100';
  const textColor = isDark ? 'text-white' : 'text-slate-800';
  const descColor = isDark ? 'text-slate-300' : 'text-slate-600';
  const buttonBg = isDark ? 'bg-slate-800 text-slate-200 hover:bg-slate-700 hover:text-white border-slate-600' : 'bg-white text-slate-800 hover:bg-gray-50 border-slate-200';

  return (
    <div className="w-full relative py-6" ref={sectionRef}>
      {/* Navigation Buttons */}
      <button 
        onClick={() => handleScroll('left')} 
        className={`hidden md:flex absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 z-20 w-14 h-14 ${buttonBg} rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 border`}
        aria-label="Previous"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
      </button>

      <button 
        onClick={() => handleScroll('right')} 
        className={`hidden md:flex absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 z-20 w-14 h-14 ${buttonBg} rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.12)] items-center justify-center hover:scale-110 active:scale-95 transition-all duration-300 border`}
        aria-label="Next"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
      </button>

      {/* Cards Container */}
      <div 
        ref={scrollRef} 
        className="flex gap-6 md:gap-8 overflow-x-auto snap-x snap-mandatory scroll-smooth py-10 px-4 md:px-0 hide-scrollbar"
        style={{ scrollPadding: '0 1rem' }}
      >
        {data.map((item, index) => (
          <div 
            key={index} 
            className={`snap-center shrink-0 w-[280px] sm:w-[320px] md:w-[360px] transform transition-all duration-700 ease-out flex flex-col`}
            style={{ 
              opacity: isVisible ? 1 : 0, 
              transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
              transitionDelay: `${index * 100}ms` 
            }}
          >
            <div className={`relative flex-1 group ${cardBg} rounded-3xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 overflow-hidden`}>
              
              {/* Top Gradient Accent Line */}
              <div className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${colors.bgFrom} ${colors.bgTo} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out`} />

              {/* Background Glow Effect on Hover */}
              <div className={`absolute -inset-2 bg-gradient-to-r ${colors.bgFrom} ${colors.bgTo} opacity-0 group-hover:opacity-5 blur-2xl transition-opacity duration-500 rounded-full`} />

              {/* Icon / Image Wrapper */}
              <div className="relative mb-8 flex justify-center">
                {item.phote ? (
                  <div className="relative z-10 w-28 h-28 rounded-2xl overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:-rotate-3">
                    <img src={item.phote} alt={item.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                ) : (
                  <div className={`relative z-10 w-24 h-24 rounded-2xl bg-gradient-to-br ${colors.bgFrom} ${colors.bgTo} flex flex-col items-center justify-center text-white shadow-lg ${colors.glow} transform group-hover:rotate-6 group-hover:scale-110 transition-all duration-500 ease-bounce`}>
                    <FontAwesomeIcon icon={icon} className="text-4xl drop-shadow-md" />
                    {/* Add a subtle inner shine */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent rounded-2xl pointer-events-none"></div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="relative z-10 text-center flex-1 flex flex-col">
                <h3 className={`text-2xl font-bold ${textColor} mb-4 tracking-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:${colors.bgFrom} group-hover:${colors.bgTo} transition-all duration-300`}>
                  {item.title}
                </h3>
                
                {item.description && (
                  <p className={`${descColor} text-sm leading-relaxed font-medium line-clamp-4 mt-auto mb-auto`}>
                    {item.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .ease-bounce {
          transition-timing-function: cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </div>
  );
};

export default ValeurCards; 